/* eslint-disable */
import React from 'react';
import { Box, Chip, Tooltip } from '@mui/material';
import { Error as ErrorIcon, Warning, Info } from '@mui/icons-material';
import type { ValidationIssue } from '../../types/release';

interface ValidationIssuesProps {
  issues: ValidationIssue[];
}

const validationRuleExplanations: Record<string, string> = {
  product_scope_mandatory: 'Product X Tech Scope is mandatory for Story, Task, and Bug tickets. It ensures proper routing to the correct Engineering Manager and Tech Lead.',
  sw_team_mandatory: 'SW Team is mandatory for Story and Task tickets per §2.3.',
  fix_version_required: 'Story, Task, and Bug tickets must have fixVersion set for release scoping.',
  subtask_parent_fix_version: 'Sub-task\'s parent must have fixVersion set. Sub-tasks inherit release scope from their parent.',
  invalid_status: 'Ticket status may not be valid for this ticket type\'s workflow.',
  status_skip_detected: 'Ticket may have skipped workflow stages (e.g., Backlog -> Integrated).',
  multiple_escalation_labels: 'Only one escalation label should exist (SL2_REQUEST or SL3_REQUEST, not both).',
};

export const ValidationIssues: React.FC<ValidationIssuesProps> = ({ issues }) => {
  if (!issues || issues.length === 0) {
    return null;
  }

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'error':
        return <ErrorIcon fontSize="small" />;
      case 'warning':
        return <Warning fontSize="small" />;
      case 'info':
        return <Info fontSize="small" />;
      default:
        return null;
    }
  };

  const getSeverityColor = (severity: string): 'error' | 'warning' | 'info' | 'default' => {
    switch (severity) {
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      case 'info':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ mt: 1 }}>
      {issues.map((issue, index) => {
        const explanation = validationRuleExplanations[issue.rule] || issue.message;
        const severityColor = getSeverityColor(issue.severity);
        
        // Use filled variant for better contrast, with custom styling for warnings
        const chipSx = {
          mr: 0.5,
          mb: 0.5,
          // Ensure good contrast for all severity levels
          ...(severityColor === 'warning' && {
            bgcolor: '#ff9800', // Orange background
            color: '#ffffff', // White text for maximum contrast
            border: '1px solid',
            borderColor: '#f57c00', // Darker orange border
            fontWeight: 600,
          }),
          ...(severityColor === 'error' && {
            bgcolor: '#d32f2f', // Red background
            color: '#ffffff', // White text for maximum contrast
            border: '1px solid',
            borderColor: '#c62828', // Darker red border
            fontWeight: 600,
          }),
          ...(severityColor === 'info' && {
            bgcolor: '#0288d1', // Blue background
            color: '#ffffff', // White text
            border: '1px solid',
            borderColor: '#0277bd', // Darker blue border
          }),
        };
        
        const severityIcon = getSeverityIcon(issue.severity);
        return (
          <Tooltip key={index} title={explanation} arrow>
            <Chip
              {...(severityIcon && { icon: severityIcon })}
              label={issue.message}
              size="small"
              color={severityColor}
              variant={severityColor === 'warning' || severityColor === 'error' ? 'filled' : 'outlined'}
              sx={chipSx}
            />
          </Tooltip>
        );
      })}
    </Box>
  );
};
